import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, Search, Eye, CheckCircle2, XCircle,
  AlertCircle, Users, X,
} from "lucide-react";
import {
  useGetMyChapterApplicationsQuery,
  useChapterAcceptApplicationMutation,
  useChapterRejectApplicationMutation,
} from "../api/chapterApi";
import { statusColors, statusLabel } from "../../application/utils/applicationStatus";

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function safeMsg(e: any): string {
  return e?.data?.message || e?.error?.data?.message || e?.message || "Something went wrong";
}

function StatusBadge({ status }: { status: string }) {
  // The shared ladder vocabulary — a chapter leader and the secretariat must not
  // be reading different names for the same rung.
  const s = String(status || "pending").toLowerCase();
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${statusColors(s).pill}`}
      title={s}
    >
      {statusLabel(s)}
    </span>
  );
}

function StatCard({ title, value, colorClass }: { title: string; value: number; colorClass: string }) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</div>
      <div className={`text-2xl font-bold mt-1 ${colorClass}`}>{value}</div>
    </div>
  );
}

// ── Accept confirmation modal ─────────────────────────────────────────────────

function AcceptModal({
  app, onConfirm, onClose, loading,
}: { app: any; onConfirm: () => void; onClose: () => void; loading: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-bold text-gray-900">Accept Application</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {app.title} {app.surname} {app.other_names}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          Are you sure you want to accept this application? The applicant will be notified and the application will proceed to the referee review stage.
        </p>
        <div className="flex gap-3">
          <button onClick={onClose} disabled={loading}
            className="flex-1 py-2.5 border rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 bg-green-600 rounded-lg text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 flex items-center justify-center gap-2">
            {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Confirm Accept
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reject modal ─────────────────────────────────────────────────────────────

function RejectModal({
  app, onConfirm, onClose, loading,
}: { app: any; onConfirm: (r: string) => void; onClose: () => void; loading: boolean }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-bold text-gray-900">Reject Application</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {app.title} {app.surname} {app.other_names}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Reason</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="Provide a reason that will be communicated to the applicant…"
            className="mt-1.5 w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300 resize-none"
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} disabled={loading}
            className="flex-1 py-2.5 border rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40">
            Cancel
          </button>
          <button onClick={() => onConfirm(reason)} disabled={loading}
            className="flex-1 py-2.5 bg-red-600 rounded-lg text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40 flex items-center justify-center gap-2">
            {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Tab = "all" | "acknowledged" | "submitted" | "approved" | "rejected";

/**
 * Rungs where the chapter's endorsement is still the thing being waited for.
 * `awaiting_endorsements` is an older name for the same rung as `acknowledged`
 * — leaving it out hid exactly the applications the chapter was holding up.
 */
const ACTIONABLE = ["acknowledged", "awaiting_endorsements", "returned"];

/** Endorsed and gone on: with the secretariat or the committee. */
const IN_REVIEW = ["under_review", "with_committee", "submitted"];

export default function ChapterApplicationsPage() {
  const [q, setQ]                   = useState("");
  const [tab, setTab]               = useState<Tab>("all");
  const [rejectTarget, setRejectTarget] = useState<any | null>(null);
  const [acceptTarget, setAcceptTarget] = useState<any | null>(null);
  const [acceptingId, setAcceptingId]   = useState<string | null>(null);
  const [banner, setBanner] = useState<{ ok: boolean; msg: string } | null>(null);

  const { data, isLoading, isFetching, refetch } =
    useGetMyChapterApplicationsQuery(undefined);

  const [acceptApp]                        = useChapterAcceptApplicationMutation();
  const [rejectApp, { isLoading: rejecting }] = useChapterRejectApplicationMutation();

  const chapter: any = data?.chapter ?? null;
  const list: any[]  = useMemo(() => data?.data ?? [], [data]);

  // Chapters this leader ACTS for, on top of the one they hold office in. When
  // there are any, the list mixes chapters and each row must name its own.
  const actingFor = data?.acting_for ?? [];
  const showChapter = actingFor.length > 0;

  // Stats
  const counts = useMemo(() => ({
    all:          list.length,
    acknowledged: list.filter((a) => ACTIONABLE.includes(a.status)).length,
    submitted:    list.filter((a) => IN_REVIEW.includes(a.status)).length,
    approved:     list.filter((a) => a.status === "approved").length,
    rejected:     list.filter((a) => a.status === "rejected").length,
  }), [list]);

  // Filtered by tab + search
  const filtered = useMemo(() => {
    return list.filter((a) => {
      const term = q.toLowerCase();
      const matchSearch = !q
        || `${a.surname} ${a.other_names}`.toLowerCase().includes(term)
        || (a.user?.email || "").toLowerCase().includes(term)
        || (a.membership_no || "").toLowerCase().includes(term);
      const matchTab =
        tab === "all"          ? true
        : tab === "acknowledged" ? ACTIONABLE.includes(a.status)
        : tab === "submitted"  ? IN_REVIEW.includes(a.status)
        : tab === "approved"   ? a.status === "approved"
        : a.status === "rejected";
      return matchSearch && matchTab;
    });
  }, [list, tab, q]);

  function flash(ok: boolean, msg: string) {
    setBanner({ ok, msg });
    setTimeout(() => setBanner(null), 4000);
  }

  async function handleAccept() {
    if (!acceptTarget) return;
    setAcceptingId(acceptTarget.id);
    setAcceptTarget(null);
    try {
      const res = await acceptApp(acceptTarget.id).unwrap();
      flash(true, res?.message ?? "Application accepted.");
    } catch (e: any) {
      flash(false, safeMsg(e));
    } finally {
      setAcceptingId(null);
    }
  }

  async function handleReject(reason: string) {
    if (!rejectTarget) return;
    try {
      const res = await rejectApp({ id: rejectTarget.id, reason }).unwrap();
      flash(true, res?.message ?? "Application rejected.");
      setRejectTarget(null);
    } catch (e: any) {
      flash(false, safeMsg(e));
    }
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-6">

      {/* ── Banner ──────────────────────────────────────────────────────────── */}
      {banner && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-bold text-white ${banner.ok ? "bg-green-600" : "bg-red-600"}`}>
          {banner.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {banner.msg}
        </div>
      )}

      {/* ── Header card ─────────────────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-semibold text-gray-900">
              {chapter?.name ?? "Chapter"} — Applications
            </div>
            <div className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
              <Users className="w-3.5 h-3.5" />
              {chapter?.state?.name ? `${chapter.state.name} State` : "All diets"}
            </div>
            {showChapter && (
              <div className="text-sm text-amber-700 mt-1">
                Also acting for {actingFor.map((c: any) => c.name).join(", ")} — their applications
                appear below.
              </div>
            )}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Search + tabs */}
        <div className="mt-5 flex flex-col lg:flex-row gap-4 items-center">
          <div className="relative w-full lg:w-96">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email or membership no…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-200"
            />
          </div>
          <div className="flex flex-wrap gap-1 p-1 bg-gray-50 border rounded-xl w-full lg:w-auto">
            {([
              { id: "all",          label: "All" },
              { id: "acknowledged", label: "Awaiting Review" },
              { id: "submitted",    label: "Forwarded" },
              { id: "approved",     label: "Approved" },
              { id: "rejected",     label: "Rejected" },
            ] as { id: Tab; label: string }[]).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-all ${
                  tab === t.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Total"           value={counts.all}          colorClass="text-gray-900" />
        <StatCard title="Awaiting Review" value={counts.acknowledged} colorClass="text-amber-600" />
        <StatCard title="Forwarded"       value={counts.submitted}    colorClass="text-blue-600" />
        <StatCard title="Approved"        value={counts.approved}     colorClass="text-green-600" />
        <StatCard title="Rejected"        value={counts.rejected}     colorClass="text-red-600" />
      </div>

      {/* ── Table ───────────────────────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b text-gray-400 font-medium uppercase text-[11px] tracking-widest">
                <th className="py-3 px-2">#</th>
                <th className="py-3 px-4">Applicant</th>
                <th className="py-3 px-4">Contact</th>
                <th className="py-3 px-4">Category</th>
                <th className="py-3 px-4">Diet</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-400">
                    Loading applications…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-400">
                    No applications found.
                  </td>
                </tr>
              ) : (
                filtered.map((a: any, idx: number) => {
                  const canAct   = ACTIONABLE.includes(a.status) && !a.chapter_accepted_at;
                  const isAccepting = acceptingId === a.id;
                  return (
                    <tr key={a.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-2 text-gray-400 font-mono">
                        {(idx + 1).toString().padStart(2, "0")}
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-bold text-gray-800 uppercase tracking-tight">
                          {a.title} {a.surname} {a.other_names}
                        </div>
                        <div className="text-[10px] text-gray-400 font-mono">
                          STEP {a.completion_step ?? 1} • {fmtDate(a.created_at)}
                        </div>
                        {showChapter && a.chapter?.name && (
                          <div className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200">
                            <span className="text-[9px] font-black text-amber-700 uppercase tracking-wider">
                              {a.chapter.name}
                            </span>
                          </div>
                        )}
                        <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-100">
                          <span className="text-[9px] font-semibold text-indigo-400 uppercase tracking-wider">Member No</span>
                          <span className="text-[10px] font-mono font-bold text-indigo-700">{a.membership_no ?? "—"}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-gray-600">
                        <div>{a.user?.email ?? "—"}</div>
                      </td>
                      <td className="py-4 px-4">
                        {a.category?.name
                          ? <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-indigo-50 text-indigo-700 border border-indigo-100 whitespace-nowrap">{a.category.name}</span>
                          : <span className="text-gray-400 text-xs">—</span>
                        }
                      </td>
                      <td className="py-4 px-4 text-gray-600 text-xs">
                        {a.diet?.title ?? "—"}
                      </td>
                      <td className="py-4 px-4">
                        <StatusBadge status={a.status} />
                        {a.chapter_accepted_by && (
                          <div className="mt-1 text-[10px] text-gray-400">
                            Accepted by <span className="font-semibold text-gray-600">{a.chapter_accepted_by?.name ?? "—"}</span>
                            {a.chapter_accepted_at && <> • {fmtDate(a.chapter_accepted_at)}</>}
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 justify-end flex-wrap">
                          {canAct && (
                            <>
                              <button
                                onClick={() => setAcceptTarget(a)}
                                disabled={isAccepting}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-bold hover:bg-green-700 transition-all disabled:opacity-40"
                              >
                                {isAccepting
                                  ? <RefreshCw className="w-3 h-3 animate-spin" />
                                  : <CheckCircle2 className="w-3 h-3" />}
                                Accept
                              </button>
                              <button
                                onClick={() => setRejectTarget(a)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 transition-all"
                              >
                                <XCircle className="w-3 h-3" /> Reject
                              </button>
                            </>
                          )}
                          <Link
                            to={`/admin-probationer-applications/${a.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-lg hover:bg-gray-900 hover:text-white transition-all shadow-sm text-xs font-bold uppercase"
                            title="Open application"
                          >
                            <Eye className="w-3.5 h-3.5" /> Open
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Reject modal ────────────────────────────────────────────────────── */}
      {acceptTarget && (
        <AcceptModal
          app={acceptTarget}
          onConfirm={handleAccept}
          onClose={() => setAcceptTarget(null)}
          loading={!!acceptingId}
        />
      )}

      {rejectTarget && (
        <RejectModal
          app={rejectTarget}
          onConfirm={handleReject}
          onClose={() => setRejectTarget(null)}
          loading={rejecting}
        />
      )}
    </div>
  );
}
